import { Fzf, type FzfResultItem } from "fzf";

export interface ProblemType {
  code: string;
  title: string;
  status: number;
  retryable: boolean;
}

export interface ProblemDocument extends ProblemType {
  detail: string;
  partnerAction: string;
}

export const CATEGORIES = [
  "Common",
  "Compliance",
  "Listings",
  "Users",
  "Webhooks",
] as const;

export type Category = (typeof CATEGORIES)[number];

export type Filter = "All" | Category | "Retryable";

export const FILTERS: Filter[] = ["All", ...CATEGORIES, "Retryable"];

const REASON_PHRASE: Record<number, string> = {
  400: "Bad Request",
  401: "Unauthorized",
  403: "Forbidden",
  404: "Not Found",
  409: "Conflict",
  422: "Unprocessable Entity",
  429: "Too Many Requests",
  500: "Internal Server Error",
  502: "Bad Gateway",
  504: "Gateway Timeout",
};

export function categoryOf(code: string): Category {
  const prefix = code.split("_")[0];
  return (prefix.charAt(0).toUpperCase() + prefix.slice(1)) as Category;
}

export function statusText(status: number): string {
  const phrase = REASON_PHRASE[status];
  return phrase ? `${status} ${phrase}` : String(status);
}

export function problemType(code: string): string {
  return `https://problems.atlas.propertyfinder.com/${code.replaceAll("_", "-")}/`;
}

export function problemJson(problem: ProblemDocument): string {
  return JSON.stringify(
    {
      type: problemType(problem.code),
      code: problem.code,
      title: problem.title,
      detail: problem.detail,
      status: problem.status,
      retryable: problem.retryable,
      partner_action: problem.partnerAction,
    },
    null,
    2,
  );
}

export interface RankedProblem {
  problem: ProblemType;
  codeMatches?: Set<number>;
  titleMatches?: Set<number>;
}

const CODE_SCORE_BOOST = 20;

type ScoredProblem = RankedProblem & { score: number };

// Ported from pf-broker problemkit-docs matcher: independent code/title/status
// finders merged by code, code matches boosted, ties broken by catalog order.
export function searchProblems(
  base: ProblemType[],
  query: string,
): RankedProblem[] {
  const trimmed = query.trim();
  if (!trimmed) return base.map((problem) => ({ problem }));

  const codeFinder = new Fzf(base, { selector: (problem) => problem.code });
  const titleFinder = new Fzf(base, { selector: (problem) => problem.title });
  const statusFinder = new Fzf(base, {
    selector: (problem) => String(problem.status),
  });

  const catalogOrder = new Map(base.map((problem, index) => [problem.code, index]));
  const scored = new Map<string, ScoredProblem>();

  const collect = (
    results: FzfResultItem<ProblemType>[],
    boost: number,
    field: "code" | "title" | "none",
  ) => {
    for (const result of results) {
      const existing = scored.get(result.item.code);
      scored.set(result.item.code, {
        problem: result.item,
        codeMatches:
          field === "code" ? result.positions : existing?.codeMatches,
        titleMatches:
          field === "title" ? result.positions : existing?.titleMatches,
        score: Math.max(result.score + boost, existing?.score ?? -Infinity),
      });
    }
  };

  collect(codeFinder.find(trimmed), CODE_SCORE_BOOST, "code");
  collect(titleFinder.find(trimmed), 0, "title");
  collect(statusFinder.find(trimmed), 0, "none");

  return [...scored.values()]
    .sort((left, right) =>
      left.score !== right.score
        ? right.score - left.score
        : (catalogOrder.get(left.problem.code) ?? 0) -
          (catalogOrder.get(right.problem.code) ?? 0),
    )
    .map(({ problem, codeMatches, titleMatches }) => ({
      problem,
      codeMatches,
      titleMatches,
    }));
}
